// useDiaryLikes — optimistic like / unlike hook for diary cards (W3 §8).
//
// Reads the active likes list for a diary; on click, optimistically toggles
// the heart and fires POST/DELETE /api/v1/diaries/{id}/likes.  On error, the
// optimistic state is rolled back.  Own-diary is allowed per W3 §8.3.
//
// The visual ``DiaryLikeButton`` component lives in ``DiaryLikeButton.tsx``;
// keeping hook + component in separate files satisfies the
// ``react-refresh/only-export-components`` ESLint rule.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";

import { likeDiary, listLikes, unlikeDiary } from "@/lib/api/diary";
import { useAuth } from "@/lib/auth/AuthProvider";

interface UseDiaryLikesResult {
  /** Total active likes (matches server `total`). */
  count: number;
  /** Whether the current user has an active like. */
  liked: boolean;
  /** Pending state for the like/unlike mutation. */
  busy: boolean;
  /** Click handler — toggles the current state. */
  toggle: () => void;
}

export function useDiaryLikes(diaryId: string | undefined): UseDiaryLikesResult {
  const { user } = useAuth();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["diary-likes", diaryId ?? ""],
    queryFn: () => listLikes(diaryId ?? ""),
    enabled: Boolean(diaryId),
    staleTime: 15_000,
  });

  const likeMut = useMutation({
    mutationFn: () => likeDiary(diaryId ?? ""),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["diary-likes", diaryId ?? ""] });
    },
  });
  const unlikeMut = useMutation({
    mutationFn: () => unlikeDiary(diaryId ?? ""),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["diary-likes", diaryId ?? ""] });
    },
  });

  const items = useMemo(() => query.data?.items ?? [], [query.data]);
  const serverCount = query.data?.total ?? items.length;
  const liked = useMemo(() => {
    if (!user) return false;
    return items.some((it) => it.user_id === user.id && !it.withdrawn_at);
  }, [items, user]);

  // Optimistic local override: while a mutation is pending, flip the
  // derived `liked` + `count` so the heart updates instantly.
  const [pendingLike, setPendingLike] = useState<boolean | null>(null);
  const optimisticLiked = pendingLike === null ? liked : pendingLike;
  const optimisticCount = useMemo(() => {
    if (pendingLike === null) return serverCount;
    if (pendingLike === liked) return serverCount;
    return pendingLike ? serverCount + 1 : Math.max(serverCount - 1, 0);
  }, [serverCount, liked, pendingLike]);

  const toggle = useCallback(() => {
    if (!diaryId || !user) return;
    const next = !optimisticLiked;
    setPendingLike(next);
    const rollback = () => setPendingLike(null);
    const mutation = next ? likeMut : unlikeMut;
    mutation.mutate(undefined, {
      onSettled: () => {
        rollback();
        void qc.invalidateQueries({ queryKey: ["diary-likes", diaryId] });
      },
      onError: rollback,
    });
  }, [diaryId, user, optimisticLiked, likeMut, unlikeMut, qc]);

  return {
    count: optimisticCount,
    liked: optimisticLiked,
    busy: likeMut.isPending || unlikeMut.isPending,
    toggle,
  };
}
